/**
 * Sub-dialog for choosing which object types to include in the blueprint.
 * Wraps the existing `ObjectTypeCheckboxList` so the parent setup dialog can
 * reduce its main surface to a one-line summary + "Change…" button.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ObjectTypeCheckboxList } from './ObjectTypeCheckboxList';
import type {
  BlueprintObjectTypes,
  UserFacingEngineId,
} from '../types/databaseBlueprintTypes';

type Props = {
  open: boolean;
  onClose: () => void;
  engine: UserFacingEngineId;
  value: BlueprintObjectTypes;
  onChange: (next: BlueprintObjectTypes) => void;
};

export function ObjectTypesPickerDialog({
  open,
  onClose,
  engine,
  value,
  onChange,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl"
        data-testid="database-blueprint-object-types-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Included object types</DialogTitle>
          <DialogDescription>
            Pick which kinds of database objects this blueprint tracks. Rows that
            don&rsquo;t apply to the chosen engine are hidden.
          </DialogDescription>
        </DialogHeader>
        <ObjectTypeCheckboxList engine={engine} value={value} onChange={onChange} />
        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            data-testid="database-blueprint-object-types-done"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
